import React, { useMemo } from 'react';
import { Modal, View, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Text } from 'tamagui';
import { FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { WebView } from 'react-native-webview';
import { HOST } from '@api/constant';
import { selectUserId } from '@reduxLocal/selectors/userSelectors';
import { useSelector } from 'react-redux';
import useGlobalUser from '@hooks/getGlobalUser';

interface FormModalProps {
  visible: boolean;
  formId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * FormModal - Displays user data forms in a modal
 * 
 * This component loads forms via the backend form builder route
 * which handles authentication via session cookies.
 * The form is rendered in an iframe/WebView to maintain session security.
 */
const FormModal: React.FC<FormModalProps> = ({ visible, formId, onClose, onSuccess }) => {
  const theme = themeSelector();
  const [loading, setLoading] = React.useState(true);
  const userId = useSelector(selectUserId);
  const globalUserId = useGlobalUser();

  // Construct the authenticated form URL
  const formUrl = useMemo(() => {
    if (!formId) return null;

    // Use the backend form builder route which handles authentication
    // The iframe parameter tells the backend to render in iframe-friendly mode
    // Include userId from app for tracking/authentication
    const userIdParam = userId || globalUserId;
    const urlParams = new URLSearchParams({
      id: formId,
      iframe: 'true',
      ...(userIdParam && { userId: userIdParam.toString() })
    });

    return `${HOST}/forms/builder?${urlParams.toString()}`;
  }, [formId, userId, globalUserId]);

  // Handle successful form submission via postMessage
  const handleWebViewMessage = (event: any) => {
    try {
      const data = Platform.OS === 'web'
        ? event.data
        : JSON.parse(event.nativeEvent?.data || '{}');

      if (data.type === 'formSubmitted' || data.success) {
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error handling form message:', error);
    }
  };

  // Listen for postMessage events on web
  React.useEffect(() => {
    if (Platform.OS === 'web' && visible) {
      window.addEventListener('message', handleWebViewMessage);
      return () => {
        window.removeEventListener('message', handleWebViewMessage);
      };
    }
  }, [visible, onSuccess, onClose]);

  if (!formUrl) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : 'fullScreen'}
      onRequestClose={onClose}
      transparent={false}
    >
      <View style={{
        flex: 1,
        backgroundColor: theme ? '#f8f9fa' : '#1a1a1a',
      }}>
        {/* Header */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 20,
          paddingTop: Platform.OS === 'ios' ? 50 : 16,
          paddingBottom: 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
          backgroundColor: theme ? '#fff' : '#2a2a2a',
          zIndex: 10,
        }}>
          <Text style={{
            fontSize: 18,
            fontWeight: '600',
            color: theme ? '#333' : '#fff',
            flex: 1,
          }}>
            Fill out Form
          </Text>
          <Pressable
            onPress={onClose}
            style={({ pressed }) => ({
              padding: 8,
              borderRadius: 20,
              backgroundColor: pressed
                ? (theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')
                : 'transparent',
            })}
          >
            <FontAwesome name="times" size={20} color={theme ? '#333' : '#fff'} />
          </Pressable>
        </View>

        {/* Form Content */}
        <View style={{ flex: 1, position: 'relative' }}>
          {loading && (
            <View style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme ? '#f8f9fa' : '#1a1a1a',
              zIndex: 5,
            }}>
              <ActivityIndicator size="large" color="rgb(90,192,234)" />
              <Text style={{
                marginTop: 16,
                fontSize: 16,
                color: theme ? '#666' : '#999',
              }}>
                Loading form...
              </Text>
            </View>
          )}

          {Platform.OS === 'web' ? (
            <iframe
              src={formUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: theme ? '#fff' : '#1a1a1a',
              }}
              onLoad={() => setLoading(false)}
              title="Data Form"
            />
          ) : (
            <WebView
              source={{ uri: formUrl }}
              style={{ flex: 1, backgroundColor: theme ? '#fff' : '#1a1a1a' }}
              onLoad={() => setLoading(false)}
              onMessage={handleWebViewMessage}
              startInLoadingState={true}
              renderLoading={() => (
                <View style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme ? '#f8f9fa' : '#1a1a1a',
                }}>
                  <ActivityIndicator size="large" color="rgb(90,192,234)" />
                </View>
              )}
              // Enable cookies to maintain session
              sharedCookiesEnabled={true}
              thirdPartyCookiesEnabled={true}
              // Allow JavaScript for form functionality
              javaScriptEnabled={true}
              domStorageEnabled={true}
            />
          )}
        </View>
      </View>
    </Modal>
  );
};

export default FormModal;
